import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { useHeaderData } from '@/hooks/useHeaderData';
import { FeatureKey } from '@/types/subscription';


const UsageDashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const { usage, isLoading } = useUsageLimits();
  const { userBilling } = useHeaderData();

  
  const featureConfig: Record<FeatureKey, { 
    icon: React.ReactNode; 
    title: string; 
    description: string;
    accentColor: string;
  }> = {
    private_prompts: {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                stroke="#00aeef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: t('usageDashboard.features.prompts.title'),
      description: t('usageDashboard.features.prompts.description'),
      accentColor: 'bg-brand-electric-blue'
    },
    public_prompts: {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 12h18M3 12l3-3m-3 3l3 3" 
                stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
        </svg>
      ),
      title: t('usageDashboard.features.publicPrompts.title'),
      description: t('usageDashboard.features.publicPrompts.description'),
      accentColor: 'bg-emerald-500'
    },
    collections: {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" 
                stroke="#0a1f44" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: t('usageDashboard.features.collections.title'),
      description: t('usageDashboard.features.collections.description'),
      accentColor: 'bg-brand-dark-blue'
    },
    api_calls: {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" 
                stroke="#00aeef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: t('usageDashboard.features.apiCalls.title'),
      description: t('usageDashboard.features.apiCalls.description'),
      accentColor: 'bg-brand-electric-blue'
    }
  };

  /**
   * Get status badge based on usage percentage
   */
  const getStatusBadge = (percentage: number | null) => {
    if (percentage === null) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 transition-colors duration-300">
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-gray-400 dark:bg-slate-500"></span>
          Unlimited
        </span>
      );
    }
    
    if (percentage < 70) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 transition-colors duration-300">
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500 dark:bg-green-400 animate-pulse"></span>
          Healthy
        </span>
      );
    }
    
    if (percentage < 90) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 transition-colors duration-300">
          <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse"></span>
          Warning
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 transition-colors duration-300">
        <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-red-500 dark:bg-red-400 animate-pulse"></span>
        Critical
      </span>
    );
  };

  /**
   * Get progress bar color based on percentage - Using brand colors
   */
  const getProgressGradient = (percentage: number | null): string => {
    if (percentage === null) return 'bg-gray-400';
    if (percentage < 70) return 'bg-brand-electric-blue';
    if (percentage < 90) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  /**
   * Format date for display
   */
  const formatResetDate = (dateString: string | null): string => {
    if (!dateString) return t('usageDashboard.noResetDate');
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return t('usageDashboard.noResetDate');
    }
  };

  /**
   * Check if user is on Free plan
   */
  const isFreeUser = (): boolean => {
    return !userBilling || userBilling.planName === 'Free';
  };

  /**
   * Filter usage data - hide API calls for free users
   */
  const filteredUsage = usage.filter(feature => {
    if (isFreeUser() && feature.feature_key === 'api_calls') {
      return false;
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50 dark:bg-slate-900 transition-colors duration-300">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 dark:border-slate-700"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-electric-blue border-t-transparent absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn bg-gray-50 dark:bg-slate-900 min-h-screen transition-colors duration-300">
      <div className="mb-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="w-1 h-8 bg-gradient-to-b from-brand-electric-blue to-brand-dark-blue rounded-full"></div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-slate-100 tracking-tight transition-colors duration-300">
            {t('usageDashboard.title')}
          </h1>
          <div className="w-1 h-8 bg-gradient-to-b from-brand-electric-blue to-brand-dark-blue rounded-full"></div>
        </div>
        <p className="text-lg text-gray-600 dark:text-slate-300 transition-colors duration-300">
          {t('usageDashboard.subtitle')}
        </p>
      </div>

      <div className="relative mb-10 overflow-hidden rounded-2xl shadow-2xl dark:shadow-2xl dark:shadow-slate-900/50">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-electric-blue via-brand-dark-blue to-[#0a1f44]"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="relative p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-white/90 text-sm font-medium uppercase tracking-wider">
                  {t('usageDashboard.currentPlan')}
                </span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-2">
                {userBilling?.planName || 'Free'}
              </h2>
              <p className="text-white/80 text-sm">
                {isFreeUser() ? 'Basic features included' : 'Premium features unlocked'}
              </p>
            </div>
            
            {isFreeUser() && (
              <Link
                to="/billing?view=subscription"
                className="group relative inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-electric-blue font-semibold rounded-xl hover:bg-brand-light-gray transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span>{t('usageDashboard.upgradeToPro')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        {filteredUsage.map((feature) => {
          const config = featureConfig[feature.feature_key];
          const percentage = feature.percentage_used;
          const isUnlimited = feature.isUnlimited;

          return (
            <div
              key={feature.feature_key}
              className="group relative bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-slate-700 hover:border-brand-electric-blue/20 dark:hover:border-blue-400/20 hover:-translate-y-1"
            >
              
              <div className={`h-1 ${config.accentColor}`}></div>
              
              <div className="p-6">
                
                <div className="text-center mb-6">
                  <div className={`flex justify-center mb-4`}>
                    <div className={`p-3 rounded-xl bg-brand-light-gray dark:bg-slate-700 group-hover:scale-110 transition-transform duration-300`}>
                      {config.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark-blue dark:text-blue-300 mb-2 transition-colors duration-300">
                    {config.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-300 transition-colors duration-300">
                    {config.description}
                  </p>
                </div>

                <div className="mb-4 flex justify-center">
                  {getStatusBadge(percentage)}
                </div>

                {isUnlimited ? (
                  <div className="text-center py-8 bg-gradient-to-br from-gray-50 dark:from-slate-700 to-gray-100 dark:to-slate-600 rounded-xl transition-colors duration-300">
                    <div className="text-6xl font-bold bg-gradient-to-br from-gray-600 dark:from-slate-300 to-gray-800 dark:to-slate-100 bg-clip-text text-transparent mb-3">
                      ∞
                    </div>
                    <p className="text-sm font-medium text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('usageDashboard.unlimited')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                  
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mb-1 transition-colors duration-300">Current Usage</p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 transition-colors duration-300">
                          {feature.current_usage.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-slate-400 mb-1 transition-colors duration-300">Limit</p>
                        <p className="text-2xl font-semibold text-gray-600 dark:text-slate-300 transition-colors duration-300">
                          {feature.limit_value?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>
                    
                    
                    <div className="relative">
                      <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-4 overflow-hidden shadow-inner transition-colors duration-300">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ease-out relative ${getProgressGradient(percentage)}`}
                          style={{ width: `${Math.min(percentage || 0, 100)}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm pt-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${percentage && percentage >= 90 ? 'bg-red-500' : percentage && percentage >= 70 ? 'bg-yellow-500' : 'bg-brand-electric-blue'}`}></div>
                        <span className="font-semibold text-brand-dark-blue dark:text-blue-400 transition-colors duration-300">
                          {percentage !== null ? `${percentage}%` : '0%'}
                        </span>
                        <span className="text-gray-600 dark:text-slate-400 transition-colors duration-300">{t('usageDashboard.used')}</span>
                      </div>
                      <div className="text-brand-dark-blue dark:text-blue-400 font-medium transition-colors duration-300">
                        {feature.remaining !== null && feature.remaining >= 0
                          ? `${feature.remaining.toLocaleString()} left`
                          : t('usageDashboard.limitReached')}
                      </div>
                    </div>
                  </div>
                )}

                {feature.reset_period === 'monthly' && feature.next_reset_at && (
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-700 transition-colors duration-300">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300 transition-colors duration-300">
                      <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Resets {formatResetDate(feature.next_reset_at)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredUsage.length === 0 && (
        <div className="text-center py-16 bg-gradient-to-br from-gray-50 dark:from-slate-800 to-gray-100 dark:to-slate-700 rounded-2xl shadow-inner transition-colors duration-300">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-300 dark:from-slate-600 to-gray-400 dark:to-slate-500 rounded-full flex items-center justify-center transition-colors duration-300">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-3 transition-colors duration-300">
            {t('usageDashboard.noUsageData')}
          </h3>
          <p className="text-gray-600 dark:text-slate-300 max-w-md mx-auto transition-colors duration-300">
            {t('usageDashboard.startUsing')}
          </p>
        </div>
      )}

      {isFreeUser() && filteredUsage.length > 0 && (
        <div className="relative overflow-hidden rounded-xl shadow-lg dark:shadow-2xl dark:shadow-slate-900/30 border border-brand-electric-blue/20 dark:border-blue-400/20 transition-colors duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark-blue to-brand-electric-blue"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
          
          <div className="relative p-6 text-center">
            <div className="flex items-center justify-center gap-4 md:gap-6">
              <div className="hidden sm:flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-xl font-bold text-white mb-1">
                  {t('usageDashboard.needMore')}
                </h3>
                <p className="text-white/80 text-sm">
                  {t('usageDashboard.upgradeMessage')}
                </p>
              </div>
              <Link
                to="/billing?view=subscription"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand-electric-blue font-semibold rounded-lg hover:bg-brand-light-gray transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <span>{t('usageDashboard.viewPlans')}</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageDashboardPage;